import { captureException } from '@sentry/react'
import { z } from 'zod'
import axios from '@/axios-instance-node'
import {
	GetNeighborhoodsSchema,
	PaginatedNeighborhoodResponse
} from '@/features/subgeo/types.ts'
import { parseResponseData } from '@/helpers/utils'
import type { ResponseType } from '@/types.ts'

export interface HttpGetNeighborhoodsParams {
	geoCode: string
	responseType: ResponseType
	limit?: number
	page?: number
	name?: string
}

const DirectNeighborhoodArrayResponse = z.array(GetNeighborhoodsSchema)

export const httpGetNeighborhoods = async (
	params: HttpGetNeighborhoodsParams
) => {
	const requestParams: Record<string, unknown> = {
		geoCode: params.geoCode,
		responseType: params.responseType
	}

	if (params.limit !== undefined) {
		requestParams.limit = params.limit
	}

	if (params.page !== undefined) {
		requestParams.page = params.page
	}

	if (params.name !== undefined) {
		requestParams.name = params.name
	}

	const response = await axios.get('/v1/geo-mapping-neighborhoods', {
		params: requestParams
	})

	// Determine expected response format based on parameters
	const expectsPagination =
		params.limit !== undefined || params.page !== undefined
	const responseShape = expectsPagination
		? PaginatedNeighborhoodResponse
		: DirectNeighborhoodArrayResponse

	const parsedData = parseResponseData({
		responseData: response.data,
		responseShape,
		onZodError: captureException
	})

	// Normalize response to always have the paginated format
	if (Array.isArray(parsedData)) {
		return {
			meta: {
				page: 1,
				limit: parsedData.length,
				total: parsedData.length,
				totalPages: 1
			},
			data: parsedData
		}
	}

	return parsedData
}

export type HttpGetNeighborhoodsResponse = z.infer<
	typeof PaginatedNeighborhoodResponse
>
